import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import CCNavbar from '../../components/CCNavbar';
import CCFooter from '../../components/CCFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath, parentRouteHref } from '../../utils/brandRouting';
import { SUPPORT_EMAIL } from '../../data/siteContent';
import { APP_LEGAL, EFFECTIVE_DATE, PUBLISHER } from '../../data/legalContent';

function useCCTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'cardcycle');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

// Section order mirrors the approved CardCycle 0.3.0 Privacy Notice
// (RC_PUBLIC_PRIVACY_TERMS_FAQ_0.3.0) one-for-one, so the public page and the
// static in-app legal text cannot drift apart.
const TOC = [
  { id: 'scope',     title: '1. Scope' },
  { id: 'stored',    title: '2. Information stored on your device' },
  { id: 'noaccess',  title: '3. What CardCycle does not access' },
  { id: 'perms',     title: '4. Permissions' },
  { id: 'nosharing', title: '5. No sale, sharing, advertising, or analytics' },
  { id: 'exports',   title: '6. Exports and backups' },
  { id: 'retention', title: '7. Retention and deletion' },
  { id: 'security',  title: '8. Security' },
  { id: 'audience',  title: '9. Adults and international users' },
  { id: 'changes',   title: '10. Changes and contact' },
];

export default function CCPrivacy() {
  useCCTheme();
  // The parent legal routes live on the parent site, which is a separate
  // route table when this page is served on the app subdomain.
  const studioPrivacyHref = parentRouteHref('/privacy');
  const dataRequestsHref = parentRouteHref('/dpdpa');
  useDocumentMeta(routeMetaFor('/apps/cardcycle/privacy'));

  return (
    <PageWrap testId="cc-privacy-page">
      <CCNavbar />

      <header className="cc-legal-header" style={{ paddingTop: 'calc(var(--nav-h) + 40px)' }}>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow" style={{ justifyContent: 'center', color: 'var(--cc-accent)' }}>CardCycle · Legal</div>
          <h1 className="reveal d1">Privacy Notice</h1>
          <p className="reveal d2" style={{ marginTop: 8 }} data-testid="cc-privacy-effective">Effective date: {EFFECTIVE_DATE} · {APP_LEGAL.cardcycle.version}</p>
        </div>
      </header>

      <div className="wrap">
        <div className="cc-summary-box reveal">
          <div className="title">The short version — what matters most</div>
          <div className="cc-summary-grid">
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>CardCycle is local-first: it works without an account and without sending your CardCycle data to Tickle Apps.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>This version does not access SMS or messages, call logs, contacts, location, camera, microphone, or bank accounts.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>Records you create — cards, billing cycles, statements, transactions and payment evidence — can be stored locally on your device.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>Every record in CardCycle is one you entered or confirmed yourself.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>No sale of your data, no advertising, no analytics, no third-party sharing.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>Delete records, clear app data, or uninstall — Tickle Apps holds no server-side copy to recover.</span></div>
          </div>
        </div>
      </div>

      <section className="section" style={{ paddingTop: 64, background: 'var(--cc-bg)' }}>
        <div className="wrap">
          <div className="legal-layout">
            <nav className="legal-toc">
              <h6>Contents</h6>
              <ul>{TOC.map(s => <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>)}</ul>
            </nav>

            <div className="legal-body" style={{ background: 'var(--cc-surf)', borderColor: 'var(--cc-border)' }}>
              <span className="legal-meta-tag" data-testid="cc-privacy-publisher">Published by {PUBLISHER} · Ahmedabad, Gujarat, India · Support: {SUPPORT_EMAIL}</span>

              <section id="scope"><h2>1. Scope</h2>
                <p>This notice explains how <strong>CardCycle</strong> handles information on your Android device. CardCycle is designed to work without an account and without sending your CardCycle data to Tickle Apps.</p>
                <p>CardCycle is published by <strong>Tickle Apps</strong>, a sole proprietorship based in Ahmedabad, Gujarat, India. A broader notice covering all Tickle Apps products is available at <a href="https://tickleapps.com/privacy/">tickleapps.com/privacy</a>.</p>
                <p>Privacy questions: <a href="mailto:support@tickleapps.com">support@tickleapps.com</a>.</p>
              </section>

              <section id="stored"><h2>2. Information stored on your device</h2>
                <div className="callout info" style={{ background: 'rgba(2,85,230,0.06)', border: '1px solid rgba(2,85,230,0.2)' }}>
                  <i className="fas fa-lock" style={{ color: 'var(--cc-primary)' }}></i>
                  <span>Depending on the features you use, CardCycle <strong>may store the records below on your device</strong>. They are kept locally so the app can work offline — they are not uploaded to Tickle Apps.</span>
                </div>
                <table className="data-table">
                  <thead><tr><th>Data</th><th>Why it is stored</th></tr></thead>
                  <tbody>
                    <tr><td><strong>Card nicknames and issuers</strong></td><td>Lets you identify each card on your dashboard</td></tr>
                    <tr><td><strong>Masked last four digits</strong></td><td>Helps you recognise a card without entering a full card number</td></tr>
                    <tr><td><strong>Statement and due dates</strong></td><td>Powers the status dashboard and the reminder schedule</td></tr>
                    <tr><td><strong>Reminders and notification history</strong></td><td>Schedules the reminders you configure and shows which have already fired</td></tr>
                    <tr><td><strong>Settings and app-lock settings</strong></td><td>Remembers how you have configured the app</td></tr>
                    <tr><td><strong>Cycle actions</strong></td><td>Records the planning actions you take on a billing cycle</td></tr>
                    <tr><td><strong>Statement records</strong></td><td>Keeps the statements you choose to record</td></tr>
                    <tr><td><strong>Transactions</strong></td><td>Keeps the transactions you choose to record</td></tr>
                    <tr><td><strong>Payment evidence</strong></td><td>Keeps the payment evidence you record against a cycle</td></tr>
                    <tr><td><strong>Files you choose to import</strong></td><td>Restores a backup file that you selected yourself</td></tr>
                  </tbody>
                </table>
                <p>Every record in CardCycle is one you entered or confirmed yourself. CardCycle does not obtain any of it from your bank, your card issuer, or your messages.</p>
              </section>

              <section id="noaccess"><h2>3. What CardCycle does not access</h2>
                <div className="callout info" style={{ background: 'rgba(2,85,230,0.06)', border: '1px solid rgba(2,85,230,0.2)' }}>
                  <i className="fas fa-shield-halved" style={{ color: 'var(--cc-primary)' }}></i>
                  <span>This version of CardCycle <strong>does not access SMS</strong>, call logs, contacts, location, camera, microphone, or bank accounts.</span>
                </div>
                <ul>
                  <li><strong>No SMS or message access.</strong> CardCycle does not access SMS, does not monitor messages, does not run a background message scan, and does not use a notification listener.</li>
                  <li>No call logs, contacts, location, camera, or microphone access.</li>
                  <li>No connection to your bank or card issuer, and no visibility into your real balances or payment status.</li>
                  <li>CardCycle never asks for a full card number, CVV, PIN, OTP, or bank login.</li>
                </ul>
                <p>Every record in CardCycle is one you entered or confirmed yourself.</p>
              </section>

              <section id="perms"><h2>4. Permissions</h2>
                <table className="data-table">
                  <thead><tr><th>Permission</th><th>Why it is used</th></tr></thead>
                  <tbody>
                    <tr><td><strong>Notifications</strong> (optional)</td><td>Used for the reminders you configure</td></tr>
                    <tr><td><strong>Biometric or device credential</strong> (optional)</td><td>Used only when you enable app lock</td></tr>
                    <tr><td><strong>File access</strong></td><td>Used only when you choose to import, restore, export, or share a file</td></tr>
                  </tbody>
                </table>
                <p>CardCycle does not request SMS, call log, contacts, location, camera, or microphone permissions.</p>
              </section>

              <section id="nosharing"><h2>5. No sale, sharing, advertising, or analytics</h2>
                <div className="callout info" style={{ background: 'rgba(2,85,230,0.06)', border: '1px solid rgba(2,85,230,0.2)' }}>
                  <i className="fas fa-lock" style={{ color: 'var(--cc-primary)' }}></i>
                  <span>CardCycle <strong>does not sell your data, use it for advertising, or share it with third parties.</strong></span>
                </div>
                <p>This release integrates no analytics SDK, crash-reporting service, advertising network, or other third-party data processor.</p>
              </section>

              <section id="exports"><h2>6. Exports and backups</h2>
                <p>Exports are created only when you choose them. They may be stored or shared using the location or application you select.</p>
                <div className="callout warn">
                  <i className="fas fa-triangle-exclamation"></i>
                  <span>Exported files are your responsibility and are <strong>not encrypted by CardCycle</strong> unless the destination you choose protects them.</span>
                </div>
              </section>

              <section id="retention"><h2>7. Retention and deletion</h2>
                <p>CardCycle keeps local records until you delete them, clear them, clear app data, or uninstall the app.</p>
                <table className="data-table">
                  <thead><tr><th>Data</th><th>Retention</th></tr></thead>
                  <tbody>
                    <tr><td>Cards, billing cycles, statements, transactions and payment evidence</td><td>Until you delete them, clear app data, or uninstall</td></tr>
                    <tr><td>Reminders and notification history</td><td>Until you clear them, clear app data, or uninstall</td></tr>
                    <tr><td>Settings and app-lock settings</td><td>Until you clear app data or uninstall</td></tr>
                    <tr><td>Files you exported</td><td>Managed entirely by you, wherever you saved them</td></tr>
                  </tbody>
                </table>
                <p>Tickle Apps cannot recover data removed from your device, because it does not hold a server-side copy.</p>
              </section>

              <section id="security"><h2>8. Security</h2>
                <p>CardCycle uses local storage protections and may offer an optional biometric or PIN app lock.</p>
                <p>No security measure can guarantee absolute protection, especially if a device or an exported file is compromised.</p>
              </section>

              <section id="audience"><h2>9. Adults and international users</h2>
                <p>CardCycle is intended for adults aged 18 or older. It is initially intended for users in India but may be used elsewhere.</p>
                <p>Mandatory rights under applicable law are not limited by this notice. To make a data-protection request about CardCycle, see {dataRequestsHref
                  ? <a href={dataRequestsHref}>TickleApps data-protection requests</a>
                  : <Link to="/dpdpa">TickleApps data-protection requests</Link>}.</p>
              </section>

              <section id="changes"><h2>10. Changes and contact</h2>
                <p>If this notice changes materially, CardCycle will update its effective date and provide an in-app notice where appropriate.</p>
                <div className="callout info" style={{ background: 'rgba(2,85,230,0.06)', border: '1px solid rgba(2,85,230,0.2)' }}>
                  <i className="fas fa-envelope" style={{ color: 'var(--cc-primary)' }}></i>
                  <span><strong>Email:</strong> <a href="mailto:support@tickleapps.com">support@tickleapps.com</a><br/>Tickle Apps · Ahmedabad, Gujarat, India</span>
                </div>
                <p style={{ marginTop: 16, fontSize: '0.85rem' }}>CardCycle is a product of TickleApps. See the {studioPrivacyHref
                  ? <a href={studioPrivacyHref}>TickleApps legal directory</a>
                  : <Link to="/privacy">TickleApps legal directory</Link>} for the other products&apos; notices, the <Link to={brandPath('cardcycle', '/terms')}>CardCycle Terms of Use</Link>, and <Link to={brandPath('cardcycle', '/account-deletion')}>Data Deletion</Link>.</p>
              </section>
            </div>
          </div>
        </div>
      </section>

      <CCFooter />
    </PageWrap>
  );
}
